import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { ThemeContext } from 'styled-components';
import { useRecurly } from '@recurly/react-recurly';
import {
  BodyLarge400,
  BodyMedium400,
  TitleLarge500,
  TitleMedium500,
  TitleSmall500,
  TitleXSmall500,
  BodyMedium500,
  BodyLarge500,
} from '@nebula/global-styles';
import { useMediaQuery } from '@nebula/hooks';

import { ChangeBillingDateStage, changeBillingDateTransition } from '@ecs-uk/web-common-mobile-view-helpers';

import {
  OneTimePaymentCard,
  AmountDueSection,
  PayWithSection,
  PaymentIconContainer,
  PaymentIcon,
  PaymentSourceSection,
  PaymentSummarySection,
  Form,
  Label,
} from './oneTimePayment.styles';

import useExistingPaymentMethodDisplay from '../../../components/PaymentCaptureMethod/hooks/useExistingPaymentMethodDisplay';
import { getCurrencyAriaLabel } from '../../../helpers';
import useExistingPaymentMethod from '../../../components/PaymentCaptureMethod/hooks/useExistingPaymentMethod';
import { InputState, PaymentMethodTypes } from '../../../components/PaymentCaptureMethod/constants.ts';
import SecurityCodeComponent from '../../../components/PaymentCaptureMethod/PaymentCardForm/SecurityCode';
import { PaymentContext } from '../../../components/PaymentOrchestrator/PaymentContextProvider';

const OneTimePayment = ({
  daysBetween,
  oneTimePaymentValue,
  planPrice,
  selectedDateFormatted,
  onCardTokenization,
  onCardTokenizationError,
  registerCvvSubmitHandler,
  onCvvStateChange,
}) => {
  const { paymentIcon, paymentMethodDisplayText, paymentAriaLabel } = useExistingPaymentMethodDisplay();
  const { isWebview } = useContext(PaymentContext);
  const { existingPaymentType } = useExistingPaymentMethod();
  const isPaymentMethodsLoading = useSelector(state => state.billing?.loading);
  const hasSavedCard = useSelector(state => state.billing?.hasSavedCard);
  const showCvvForm = existingPaymentType === PaymentMethodTypes.Card || (isPaymentMethodsLoading && hasSavedCard);
  const recurly = useRecurly();
  const cvvFormRef = useRef(null);
  const [isCvvValid, setIsCvvValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useContext(ThemeContext);
  const isTabletLandscape = useMediaQuery(theme.media.tabletLandscapeMin);
  const BodyText = isTabletLandscape ? BodyLarge400 : BodyMedium400;
  const TitleSmallText = isTabletLandscape ? TitleSmall500 : TitleXSmall500;
  const TitleLargeText = isTabletLandscape ? TitleLarge500 : TitleMedium500;
  const BodyMediumText = isTabletLandscape ? BodyLarge500 : BodyMedium500;

  const existingMethodIdentifier = 'existing_card';
  const tokenizationTimeoutRef = useRef(null);

  useEffect(() => {
    tokenizationTimeoutRef.current = null; // Initialize the ref
    return () => {
      if (tokenizationTimeoutRef.current) {
        clearTimeout(tokenizationTimeoutRef.current);
        tokenizationTimeoutRef.current = null;
      }
    };
  }, []);
  const handleCvvSubmit = useCallback(
    event => {
      event?.preventDefault();
      // Prevent submission if CVV is not valid (user hasn't entered it yet)
      if (!recurly || !cvvFormRef.current || isSubmitting || !isCvvValid) {
        return;
      }
      // For blocking navigation on mobile app
      if (isWebview) {
        changeBillingDateTransition({ stage: ChangeBillingDateStage.PROCESSING });
      }

      setIsSubmitting(true);

      // Clear any existing timeout before creating a new one
      if (tokenizationTimeoutRef.current) {
        clearTimeout(tokenizationTimeoutRef.current);
      }

      tokenizationTimeoutRef.current = setTimeout(() => {
        setIsSubmitting(false);
        onCardTokenizationError?.(
          { message: 'Network error: Unable to process payment. Please check your connection and try again.' },
          existingMethodIdentifier
        );
      }, 60000);

      recurly.token(cvvFormRef.current, (error, token) => {
        // Clear timeout when callback is received
        if (tokenizationTimeoutRef.current) {
          clearTimeout(tokenizationTimeoutRef.current);
          tokenizationTimeoutRef.current = null;
        }

        setIsSubmitting(false);
        if (error) {
          onCardTokenizationError?.(error, existingMethodIdentifier);
          return;
        }
        onCardTokenization?.(token, existingMethodIdentifier);
      });
    },
    [recurly, isSubmitting, isCvvValid, onCardTokenizationError, onCardTokenization, isWebview]
  );

  // eslint-disable-next-line no-unused-vars
  const handleCvvButtonClick = useCallback(_event => {
    if (cvvFormRef.current?.requestSubmit) {
      cvvFormRef.current.requestSubmit();
      return;
    }

    cvvFormRef.current?.dispatchEvent(new window.Event('submit', { bubbles: true, cancelable: true }));
  }, []);

  useEffect(() => {
    if (showCvvForm) {
      registerCvvSubmitHandler?.(() => handleCvvButtonClick());
    } else {
      registerCvvSubmitHandler?.(null);
    }
  }, [showCvvForm, handleCvvButtonClick, registerCvvSubmitHandler]);

  useEffect(() => {
    onCvvStateChange?.({
      isValid: isCvvValid,
      isSubmitting,
    });
  }, [showCvvForm, isCvvValid, isSubmitting, onCvvStateChange]);
  const paymentSummaryAriaLabel = `You'll go back to a monthly subscription of ${getCurrencyAriaLabel(
    planPrice
  )} from ${selectedDateFormatted}.`;

  return (
    <OneTimePaymentCard cardType="elevated">
      <TitleLargeText as="h2">One-time payment</TitleLargeText>
      <BodyText>
        This is to cover the {daysBetween} {daysBetween === 1 ? 'day' : 'days'} between your current and chosen billing
        date.
      </BodyText>
      <AmountDueSection as="section" aria-label={`Amount due ${getCurrencyAriaLabel(oneTimePaymentValue)}`}>
        <TitleSmallText aria-hidden="true">Amount due</TitleSmallText>
        <TitleSmallText aria-hidden="true">
          £{oneTimePaymentValue != null ? oneTimePaymentValue.toFixed(2) : ''}
        </TitleSmallText>
      </AmountDueSection>
      <PayWithSection>
        <BodyText>Pay with</BodyText>
        <PaymentSourceSection aria-label={paymentAriaLabel}>
          <PaymentIconContainer aria-hidden="true">
            <PaymentIcon src={paymentIcon} alt="" />
          </PaymentIconContainer>
          <TitleSmallText aria-hidden="true">{paymentMethodDisplayText}</TitleSmallText>
        </PaymentSourceSection>
      </PayWithSection>
      {showCvvForm && (
        <Form
          id="recurly_cvv_form"
          ref={cvvFormRef}
          onSubmit={handleCvvSubmit}
          data-automation-test-element="recurly-cvv-form"
          style={{ pointerEvents: isSubmitting ? 'none' : 'auto' }}
        >
          <Label>
            <BodyMediumText>Security code</BodyMediumText>
          </Label>
          <SecurityCodeComponent
            isBillDateChange
            onSecurityCodeStateChange={state => setIsCvvValid(state === InputState.Success)}
            onFormValidityChange={setIsCvvValid}
          />
        </Form>
      )}
      <PaymentSummarySection as="section" aria-label={paymentSummaryAriaLabel}>
        <BodyText aria-hidden="true">
          You&apos;ll go back to a monthly subscription of £{planPrice} from {selectedDateFormatted}.
        </BodyText>
      </PaymentSummarySection>
    </OneTimePaymentCard>
  );
};

OneTimePayment.propTypes = {
  daysBetween: PropTypes.number.isRequired,
  oneTimePaymentValue: PropTypes.number,
  planPrice: PropTypes.number.isRequired,
  selectedDateFormatted: PropTypes.string.isRequired,
  onCardTokenization: PropTypes.func,
  onCardTokenizationError: PropTypes.func,
  registerCvvSubmitHandler: PropTypes.func,
  onCvvStateChange: PropTypes.func,
};

export default OneTimePayment;
